// ═══════════════════════════════════════════════════════════
// MAIN GAME CLASS - Core Game Loop & State Management
// ═══════════════════════════════════════════════════════════

import { GameState, GameConfig, Bounds, Vector2, DifficultyConfig, CornerType } from './types.js';
import { Ball } from './Ball.js';
import { ParticleSystem } from './Particle.js';
import { CornerManager } from './Corner.js';
import { AudioManager } from './Audio.js';
import { UI } from './UI.js';
import { Utils } from './Utils.js';
import { ObstacleManager } from './Obstacle.js';
import { BoostManager } from './Boost.js';
import { PointPickupManager } from './PointPickup.js';
import { Theme, drawCyberGrid } from './Theme.js';

export class Game {
    private canvas: HTMLCanvasElement;
    private ctx: CanvasRenderingContext2D;
    private config: GameConfig;

    private state: GameState = GameState.MENU;
    private ball: Ball;
    private particles: ParticleSystem;
    private corners: CornerManager;
    private audio: AudioManager;
    private ui: UI;
    private obstacles: ObstacleManager;
    private boosts: BoostManager;
    private pointPickups: PointPickupManager;

    private score: number = 0;
    private cornerHits: number = 0;
    private timeRemaining: number = 30;
    private lastTime: number = 0;
    private isNewHighScore: boolean = false;
    private currentLevel: number = 1;
    private difficulty: DifficultyConfig;

    // Screen shake
    private shakeIntensity: number = 0;
    private shakeDecay: number = 0.9;
    private cameraOffset: Vector2 = { x: 0, y: 0 };

    // Parallax cyber grid background offsets
    private gridOffsetFar: number = 0;
    private gridOffsetMid: number = 0;
    private gridOffsetNear: number = 0;

    // Input
    private keys: Set<string> = new Set();
    private mouseDown: boolean = false;

    // Last second for tick sound
    private lastSecond: number = 0;

    // Corner hit tracking to prevent duplicate scoring
    private lastHitCorner: CornerType | null = null;

    constructor(canvas: HTMLCanvasElement) {
        this.canvas = canvas;
        const ctx = canvas.getContext('2d');
        if (!ctx) throw new Error('Could not get 2D context');
        this.ctx = ctx;

        // Setup canvas
        this.setupCanvas();

        // Initialize config
        this.config = {
            canvasWidth: this.canvas.width,
            canvasHeight: this.canvas.height,
            ballSize: 20,
            ballSpeed: 300,
            cornerHitRadius: 60, // Base radius (will be adjusted by difficulty)
            initialTime: 30,
            timeBonus: 10,
            trailLength: 15,
            particleCount: 30
        };

        // Initialize difficulty (level 1 - 0 corner hits)
        this.difficulty = this.calculateDifficulty(0);

        // Initialize game objects
        // All game objects use full canvas bounds (no HUD offset)
        const gameBounds: Bounds = {
            width: this.config.canvasWidth,
            height: this.config.canvasHeight
        };

        this.ball = new Ball(gameBounds, this.config.ballSize, this.config.ballSpeed, this.difficulty.aimOscillationSpeed);
        this.particles = new ParticleSystem(gameBounds);
        this.corners = new CornerManager(gameBounds, this.difficulty.cornerHitRadius);
        this.audio = new AudioManager();
        this.ui = new UI(gameBounds);
        this.obstacles = new ObstacleManager(gameBounds);
        this.boosts = new BoostManager(gameBounds);
        this.pointPickups = new PointPickupManager(gameBounds);

        // Setup input handlers
        this.setupInputHandlers();

        // Start game loop
        this.lastTime = performance.now();
        requestAnimationFrame(this.gameLoop.bind(this));
    }

    private setupCanvas(): void {
        // Detect mobile device
        const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);

        let targetWidth: number;
        let targetHeight: number;

        if (isMobile) {
            // Mobile: Force portrait dimensions (9:16 aspect ratio)
            const PORTRAIT_WIDTH = 440;
            const PORTRAIT_HEIGHT = 780;
            const PORTRAIT_ASPECT_RATIO = PORTRAIT_WIDTH / PORTRAIT_HEIGHT; // ~0.56:1

            targetWidth = PORTRAIT_WIDTH;
            targetHeight = PORTRAIT_HEIGHT;

            // Fit to available viewport while maintaining portrait aspect ratio
            const maxWidth = Math.min(window.innerWidth * 0.95, 420);
            const maxHeight = window.innerHeight * 0.90;

            if (maxWidth / maxHeight > PORTRAIT_ASPECT_RATIO) {
                // Height is the limiting factor
                targetHeight = Math.min(maxHeight, PORTRAIT_HEIGHT);
                targetWidth = targetHeight * PORTRAIT_ASPECT_RATIO;
            } else {
                // Width is the limiting factor
                targetWidth = Math.min(maxWidth, PORTRAIT_WIDTH);
                targetHeight = targetWidth / PORTRAIT_ASPECT_RATIO;
            }
        } else {
            // Desktop: Use full available space (no aspect ratio constraint)
            targetWidth = window.innerWidth * 0.90;
            targetHeight = window.innerHeight * 0.90;
        }

        this.canvas.width = Math.floor(targetWidth);
        this.canvas.height = Math.floor(targetHeight);

        // Canvas visual size matches logical size (no additional scaling)
        this.canvas.style.width = `${Math.floor(targetWidth)}px`;
        this.canvas.style.height = `${Math.floor(targetHeight)}px`;

        // Enable image smoothing for smoother animation
        this.ctx.imageSmoothingEnabled = true;
    }

    private setupInputHandlers(): void {
        // Keyboard
        window.addEventListener('keydown', (e) => {
            this.keys.add(e.code);

            if (e.code === 'Space') {
                e.preventDefault();
                this.handlePressDown();
            }
        });

        window.addEventListener('keyup', (e) => {
            this.keys.delete(e.code);

            if (e.code === 'Space') {
                this.handlePressUp();
            }
        });

        // Mouse
        this.canvas.addEventListener('mousedown', (e) => {
            this.mouseDown = true;
            this.handlePressDown();
        });

        this.canvas.addEventListener('mouseup', () => {
            this.mouseDown = false;
            this.handlePressUp();
        });

        // Touch support
        this.canvas.addEventListener('touchstart', (e) => {
            e.preventDefault();
            this.handlePressDown();
        });

        this.canvas.addEventListener('touchend', (e) => {
            e.preventDefault();
            this.handlePressUp();
        });

        this.canvas.addEventListener('touchcancel', (e) => {
            e.preventDefault();
            this.handlePressUp();
        });

        // Window resize
        window.addEventListener('resize', () => {
            this.setupCanvas();
        });
    }

    private handlePressDown(): void {
        if (this.state === GameState.MENU) {
            this.startGame();
        } else if (this.state === GameState.PLAYING) {
            // Start aiming
            this.ball.startAiming();
        } else if (this.state === GameState.GAME_OVER) {
            this.restartGame();
        }
    }

    private handlePressUp(): void {
        if (this.state === GameState.PLAYING && this.ball.isAiming()) {
            // Release and launch ball in aimed direction
            this.ball.release();
            this.audio.playLaunch(); // Play whoosh sound
            this.addScreenShake(5);
        }
    }

    private startGame(): void {
        this.state = GameState.PLAYING;
        this.score = 0;
        this.cornerHits = 0;
        this.timeRemaining = this.config.initialTime;
        this.isNewHighScore = false;
        this.lastHitCorner = null; // Reset corner hit tracking

        // Reset difficulty to level 1
        this.difficulty = this.calculateDifficulty(0);
        this.currentLevel = 1;

        // Game area uses full canvas (no HUD offset)
        const gameBounds: Bounds = {
            width: this.config.canvasWidth,
            height: this.config.canvasHeight
        };

        this.ball.reset(gameBounds);
        this.ball.updateOscillationSpeed(this.difficulty.aimOscillationSpeed);
        this.particles.clear();
        this.corners.reset();
        this.corners.updateHitRadius(this.difficulty.cornerHitRadius);

        // Generate obstacles based on difficulty
        this.obstacles.regenerateWithDifficulty(this.currentLevel);

        // Reset boosts
        this.boosts.reset();

        // Reset and spawn initial point pickups
        this.pointPickups.reset();
        this.pointPickups.spawnInitial(3);

        this.audio.playStart();
        // Optional: Start background music
        // this.audio.startBackgroundMusic();
    }

    private restartGame(): void {
        this.startGame();
    }

    private gameLoop(currentTime: number): void {
        const deltaTime = Math.min((currentTime - this.lastTime) / 1000, 0.1);
        this.lastTime = currentTime;

        this.update(deltaTime);
        this.render();

        requestAnimationFrame(this.gameLoop.bind(this));
    }

    private update(deltaTime: number): void {
        if (this.state === GameState.PLAYING) {
            this.updatePlaying(deltaTime);
        } else if (this.state === GameState.MENU) {
            this.ui.update(deltaTime);
        } else if (this.state === GameState.GAME_OVER) {
            this.ui.update(deltaTime);
        }

        // Always update particles for visual continuity
        this.particles.update(deltaTime);

        // Update screen shake
        this.updateScreenShake(deltaTime);
    }

    private updatePlaying(deltaTime: number): void {
        // Update ball
        this.ball.update(deltaTime);

        // Check boundary collision
        const collision = this.ball.checkBoundaryCollision();
        if (collision.collided && collision.normal) {
            this.audio.playBounce();
            this.particles.emitBounce(this.ball.position, collision.normal);
            this.addScreenShake(2);
        }

        // Update corners
        this.corners.update(deltaTime, this.ball.position);

        // Update obstacles
        this.obstacles.update(deltaTime);

        // Update boosts
        this.boosts.update(deltaTime);

        // Update point pickups
        this.pointPickups.update(deltaTime);

        // Check boost collision
        if (this.boosts.checkCollision(this.ball.position, this.config.ballSize)) {
            this.ball.activateBoost();
            this.audio.playCornerHit(); // Use corner hit sound for now
            this.particles.emitCornerHit(this.ball.position, 20);
            this.addScreenShake(5);
        }

        // Check point pickup collision
        const collectedPickup = this.pointPickups.checkCollision(this.ball.position, this.config.ballSize);
        if (collectedPickup) {
            this.score += collectedPickup.value;
            this.audio.playBounce(); // Use bounce sound for pickup
            this.particles.addScorePopup(collectedPickup.position, collectedPickup.value, false);
            this.particles.emitCornerHit(collectedPickup.position, 15);
            this.addScreenShake(3);
        }

        // Check obstacle collision
        if (this.obstacles.checkCollision(this.ball.position, this.config.ballSize)) {
            this.gameOverFromObstacle();
            return; // Exit early, game is over
        }

        // Check if ball has left all corner areas
        const isInAnyCorner = this.corners.isInAnyCorner(this.ball.position);
        if (!isInAnyCorner) {
            // Ball has left all corners, allow new corner hits
            this.lastHitCorner = null;
        }

        // Check corner hit
        const hitCorner = this.corners.checkCornerHit(this.ball.position);
        if (hitCorner && hitCorner.type !== this.lastHitCorner) {
            // Only score if this is a different corner than the last one hit
            this.score += 100;
            this.cornerHits++;
            this.timeRemaining += this.config.timeBonus;
            this.audio.playCornerHit();
            this.particles.emitCornerHit(this.ball.position, this.config.particleCount);
            this.particles.addScorePopup(this.ball.position, 100, true, hitCorner.type);
            this.addScreenShake(8);

            // Mark this corner as last hit
            this.lastHitCorner = hitCorner.type;

            // Update difficulty based on corner hits
            this.updateDifficulty();
        }

        // Update time
        this.timeRemaining -= deltaTime;

        // Tick sound every second when time is low
        const currentSecond = Math.floor(this.timeRemaining);
        if (this.timeRemaining < 10 && currentSecond !== this.lastSecond) {
            this.audio.playTick();
            this.lastSecond = currentSecond;
        }

        // Warning sound at 5 seconds
        if (this.timeRemaining < 5 && this.timeRemaining > 4.9) {
            this.audio.playWarning();
        }

        // Game over check
        if (this.timeRemaining <= 0) {
            this.gameOver();
        }

        // Update UI
        this.ui.update(deltaTime);
    }

    private gameOver(): void {
        this.state = GameState.GAME_OVER;
        this.timeRemaining = 0;

        // Check and save high score
        const previousHighScore = this.ui['highScore'];
        this.ui.saveHighScore(this.score);
        this.isNewHighScore = this.score > previousHighScore && this.score > 0;

        this.audio.playGameOver();
        this.audio.stopBackgroundMusic();

        // Final explosion
        this.particles.emitCornerHit(this.ball.position, 50);
        this.addScreenShake(15);
    }

    private gameOverFromObstacle(): void {
        this.state = GameState.GAME_OVER;
        this.timeRemaining = 0;

        // Check and save high score
        const previousHighScore = this.ui['highScore'];
        this.ui.saveHighScore(this.score);
        this.isNewHighScore = this.score > previousHighScore && this.score > 0;

        this.audio.playGameOver();
        this.audio.stopBackgroundMusic();

        // Explosion at obstacle collision point
        this.particles.emitCornerHit(this.ball.position, 60);
        this.addScreenShake(20);
    }

    private calculateDifficulty(cornerHits: number): DifficultyConfig {
        // Calculate level (every 3 corner hits = 1 level)
        const level = Math.floor(cornerHits / 3) + 1;

        // Corner hit radius: starts at 60 (easy) and decreases to 25 (hard)
        // Decreases by 3 pixels per level, minimum 25
        const cornerHitRadius = Math.max(25, 60 - (level - 1) * 3);

        // Aim oscillation speed: starts at 1.2 (slow/easy) and increases to 3.5 (fast/hard)
        // Increases by 0.15 per level, maximum 3.5
        const aimOscillationSpeed = Math.min(3.5, 1.2 + (level - 1) * 0.15);

        return {
            level,
            cornerHitRadius,
            aimOscillationSpeed
        };
    }

    private updateDifficulty(): void {
        const newDifficulty = this.calculateDifficulty(this.cornerHits);

        // Check if level has changed
        if (newDifficulty.level !== this.difficulty.level) {
            this.difficulty = newDifficulty;
            this.currentLevel = newDifficulty.level;

            // Update corner manager with new hit radius
            this.corners.updateHitRadius(this.difficulty.cornerHitRadius);

            // Update ball with new oscillation speed
            this.ball.updateOscillationSpeed(this.difficulty.aimOscillationSpeed);

            // Regenerate obstacles with new difficulty
            this.obstacles.regenerateWithDifficulty(this.currentLevel);
        }
    }

    private updateScreenShake(deltaTime: number): void {
        if (this.shakeIntensity > 0) {
            this.cameraOffset = Utils.shakeAmount(this.shakeIntensity, this.shakeDecay);
            this.shakeIntensity *= this.shakeDecay;

            // Stop shake when intensity is very low
            if (this.shakeIntensity < 0.1) {
                this.shakeIntensity = 0;
                this.cameraOffset = { x: 0, y: 0 };
            }
        }
    }

    private addScreenShake(intensity: number): void {
        this.shakeIntensity = Math.max(this.shakeIntensity, intensity);
    }

    private render(): void {
        // Clear canvas with deep grid black
        this.ctx.fillStyle = Theme.background.baseColor;
        this.ctx.fillRect(0, 0, this.canvas.width, this.canvas.height);

        // Render parallax cyber grid background
        this.renderCyberGridBackground();

        // Apply screen shake
        this.ctx.save();
        this.ctx.translate(this.cameraOffset.x, this.cameraOffset.y);

        if (this.state === GameState.PLAYING) {
            // Render corners
            this.corners.render(this.ctx);

            // Render obstacles
            this.obstacles.render(this.ctx);

            // Render boosts
            this.boosts.render(this.ctx);

            // Render point pickups
            this.pointPickups.render(this.ctx);

            // Render ball
            this.ball.render(this.ctx);

            // Render particles
            this.particles.render(this.ctx);

            // Update HTML HUD (outside canvas)
            this.ui.updateHUD(this.score, this.timeRemaining);

        } else if (this.state === GameState.MENU) {
            // Render menu
            this.ui.renderMenu(this.ctx);
            // Hide HTML HUD during menu
            this.ui.hideHUD();

        } else if (this.state === GameState.GAME_OVER) {
            // Still render game elements in background
            this.corners.render(this.ctx);
            this.obstacles.render(this.ctx);
            this.boosts.render(this.ctx);
            this.pointPickups.render(this.ctx);
            this.ball.render(this.ctx);
            this.particles.render(this.ctx);

            // Render game over overlay
            this.ui.renderGameOver(this.ctx, this.score, this.isNewHighScore);
            // Hide HTML HUD during game over
            this.ui.hideHUD();
        }

        this.ctx.restore();

        // Render border
        this.renderBorder();

        // Render CRT effects
        this.renderCRTEffects();
    }

    /**
     * Render parallax cyber grid background
     */
    private renderCyberGridBackground(): void {
        // Skip on mobile for performance
        const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
        if (isMobile) return;

        // Update grid offsets based on ball velocity for parallax effect
        if (this.state === GameState.PLAYING) {
            const ballSpeed = Math.sqrt(
                this.ball.velocity.x * this.ball.velocity.x +
                this.ball.velocity.y * this.ball.velocity.y
            );

            // Normalize speed for consistent scrolling
            const normalizedSpeed = ballSpeed / 300; // Base speed is 300

            this.gridOffsetFar += Theme.background.farLayer.scrollSpeed * normalizedSpeed;
            this.gridOffsetMid += Theme.background.midLayer.scrollSpeed * normalizedSpeed;
            this.gridOffsetNear += Theme.background.nearLayer.scrollSpeed * normalizedSpeed;
        }

        // Draw far layer (slowest)
        drawCyberGrid(
            this.ctx,
            this.canvas.width,
            this.canvas.height,
            Theme.background.farLayer.gridSize,
            Theme.background.farLayer.color,
            Theme.background.farLayer.lineWidth,
            this.gridOffsetFar
        );

        // Draw mid layer
        drawCyberGrid(
            this.ctx,
            this.canvas.width,
            this.canvas.height,
            Theme.background.midLayer.gridSize,
            Theme.background.midLayer.color,
            Theme.background.midLayer.lineWidth,
            this.gridOffsetMid
        );

        // Draw near layer (fastest)
        drawCyberGrid(
            this.ctx,
            this.canvas.width,
            this.canvas.height,
            Theme.background.nearLayer.gridSize,
            Theme.background.nearLayer.color,
            Theme.background.nearLayer.lineWidth,
            this.gridOffsetNear
        );
    }

    private renderBorder(): void {
        // Enhanced neon border
        this.ctx.save();
        this.ctx.shadowColor = Theme.colors.neonCyan;
        this.ctx.shadowBlur = 10;
        this.ctx.strokeStyle = Theme.colors.neonCyan;
        this.ctx.lineWidth = 4;
        this.ctx.strokeRect(2, 2, this.canvas.width - 4, this.canvas.height - 4);
        this.ctx.restore();
    }

    private renderCRTEffects(): void {
        // Skip expensive effects on mobile
        const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);

        if (!isMobile) {
            // Subtle RGB pixel grid (phosphor glow effect)
            this.ctx.save();
            this.ctx.globalAlpha = 0.03;
            this.ctx.globalCompositeOperation = 'lighter';

            // Draw subtle pixel grid pattern (sparse for performance)
            const gridSize = 4;
            const skipFactor = 2; // Only draw every other row for performance
            for (let y = 0; y < this.canvas.height; y += gridSize * skipFactor) {
                for (let x = 0; x < this.canvas.width; x += gridSize) {
                    const offset = (x + y) % 2;
                    if (offset === 0) {
                        this.ctx.fillStyle = '#ff0000';
                        this.ctx.fillRect(x, y, 1, 1);
                        this.ctx.fillStyle = '#00ff00';
                        this.ctx.fillRect(x + 1, y, 1, 1);
                        this.ctx.fillStyle = '#0000ff';
                        this.ctx.fillRect(x + 2, y, 1, 1);
                    }
                }
            }

            this.ctx.restore();
        }

        // Subtle screen flicker/noise (on all devices)
        this.ctx.save();
        this.ctx.globalAlpha = 0.02;
        const noise = Math.random() * 10;
        this.ctx.fillStyle = noise > 9.5 ? '#ffffff' : 'transparent';
        this.ctx.fillRect(0, 0, this.canvas.width, this.canvas.height);
        this.ctx.restore();
    }
}
